import React, { useState } from 'react';
import { X, CheckCircle, Loader2, AlertCircle, UserCheck, KeyRound, RefreshCw } from 'lucide-react';

type SubmittedData = {
  orderNumber: string;
  customerName: string;
  totalAmount: string;
};

export function GuestReservationFormSuccessView({
  t,
  confirmationTemplate,
  submittedReservationData,
  submittedReservationId,
  cloverCharge,
  accountCreationStatus,
  accountCreationError,
  onRetryAccountCreation,
  onCloseSuccess,
}: {
  t: (key: string) => string;
  confirmationTemplate: { subject?: string; content: string } | null;
  submittedReservationData: SubmittedData | null;
  submittedReservationId: string | null;
  cloverCharge?: { chargeId: string; amount: number; status: string } | null;
  accountCreationStatus?: 'idle' | 'creating' | 'done' | 'error';
  accountCreationError?: string | null;
  onRetryAccountCreation?: (newPassword: string) => Promise<void>;
  onCloseSuccess: () => void;
}) {
  const data = submittedReservationData;
  const [retryPassword, setRetryPassword] = useState('');
  const [retryConfirm, setRetryConfirm] = useState('');
  const [retryValidationError, setRetryValidationError] = useState<string | null>(null);

  return (
    <div className="h-full w-full bg-white border-l border-gray-200 shadow-lg overflow-hidden flex flex-col">
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-nature-50 to-white flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">{t('guestReservation.confirmation.title')}</h2>
          <button
            type="button"
            onClick={onCloseSuccess}
            className="text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="bg-green-100 rounded-full p-6 mb-6">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>

            {/* Clover payment success badge */}
            {cloverCharge && (
              <div className="flex items-center gap-2 mb-5 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm font-medium w-full max-w-md">
                <CheckCircle className="w-4 h-4 shrink-0 text-green-600" />
                <span>
                  Paiement réussi —{' '}
                  {(cloverCharge.amount / 100).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}
                </span>
                <span className="ml-auto text-xs text-green-600 font-mono truncate">{cloverCharge.chargeId}</span>
              </div>
            )}

            {/* Account creation status */}
            {accountCreationStatus === 'creating' && (
              <div className="flex items-center gap-2 mb-5 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-blue-800 text-sm w-full max-w-md">
                <Loader2 className="w-4 h-4 shrink-0 text-blue-600 animate-spin" />
                <span>Création de votre compte en cours…</span>
              </div>
            )}
            {accountCreationStatus === 'done' && (
              <div className="flex items-center gap-2 mb-5 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm font-medium w-full max-w-md">
                <UserCheck className="w-4 h-4 shrink-0 text-green-600" />
                <span>
                  Compte créé ! Vérifiez votre courriel pour activer votre accès.
                </span>
              </div>
            )}
            {accountCreationStatus === 'error' && (
              <div className="mb-5 w-full max-w-md space-y-3">
                <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
                  <span>{accountCreationError ?? 'La création du compte a échoué.'}</span>
                </div>
                {onRetryAccountCreation && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3 text-left">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                      <KeyRound className="w-4 h-4 text-nature-600" />
                      Choisir un nouveau mot de passe
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Nouveau mot de passe</label>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={retryPassword}
                        onChange={(e) => { setRetryPassword(e.target.value); setRetryValidationError(null); }}
                        minLength={8}
                        placeholder="Minimum 8 caractères"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nature-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Confirmer le mot de passe</label>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={retryConfirm}
                        onChange={(e) => { setRetryConfirm(e.target.value); setRetryValidationError(null); }}
                        placeholder="Répétez votre mot de passe"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nature-500"
                      />
                    </div>
                    {retryValidationError && (
                      <p className="text-xs text-red-600">{retryValidationError}</p>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        if (retryPassword.length < 8) {
                          setRetryValidationError('Le mot de passe doit contenir au moins 8 caractères.');
                          return;
                        }
                        if (retryPassword !== retryConfirm) {
                          setRetryValidationError('Les mots de passe ne correspondent pas.');
                          return;
                        }
                        setRetryValidationError(null);
                        await onRetryAccountCreation(retryPassword);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-2 bg-nature-600 hover:bg-nature-700 text-white rounded-lg text-sm font-semibold transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Créer mon compte
                    </button>
                  </div>
                )}
              </div>
            )}

            {confirmationTemplate && data ? (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {confirmationTemplate.subject
                    ? confirmationTemplate.subject
                        .replace(/\{orderNumber\}/g, data.orderNumber)
                        .replace(/\{customerName\}/g, data.customerName)
                        .replace(/\{totalAmount\}/g, data.totalAmount)
                    : t('guestReservation.confirmation.title')}
                </h3>
                <div className="bg-gray-50 rounded-lg p-6 mb-6 w-full max-w-md text-left">
                  <div className="whitespace-pre-wrap text-gray-700 mb-4 text-base leading-relaxed">
                    {confirmationTemplate.content
                      .replace(/\{orderNumber\}/g, data.orderNumber)
                      .replace(/\{customerName\}/g, data.customerName)
                      .replace(/\{totalAmount\}/g, data.totalAmount)
                      .replace(/\\n/g, '\n')}
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-blue-800">
                      <strong>{t('guestReservation.confirmation.reservationId')}:</strong>{' '}
                      <span className="font-mono">{data.orderNumber}</span>
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('guestReservation.confirmation.title')}</h3>
                <p className="text-gray-600 mb-6">{t('guestReservation.confirmation.subtitle')}</p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6 w-full max-w-md">
                  <p className="text-sm text-gray-600 mb-1">{t('guestReservation.confirmation.reservationId')}</p>
                  <p className="font-mono font-semibold text-gray-900 text-lg">{submittedReservationId}</p>
                </div>
                <p className="text-sm text-gray-500">{t('guestReservation.confirmation.statusMessage')}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
